import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBilling } from '../contexts/BillingContext';
import { PLAN_CONFIG } from '../config/stripeConfig';
import { getProMonthlyEmailLimit } from '../config/proEmailLimits';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Bell, MoonStar, SunMedium } from 'lucide-react';
import { subscriptionService } from '../services/subscriptionService';
import WorkspaceSelectionModal from './WorkspaceSelectionModal';
import SlidingAddContactModal from './SlidingAddContactModal';
import UpgradeModal from './UpgradeModal';
import { contactService } from '../services/contactService';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { referralService } from '../services/referralService';
import { NotificationService } from '../services/notificationService';
import { useSidebar } from '../contexts/SidebarContext';
// import ScubamailAIButton from './ScubamailAIButton';
// import ScubamailAIModal from './ScubamailAIModal';
import NotesModal from './NotesModal';
import NotificationCenter from './NotificationCenter';

// CSS animations for mobile dropdown and notification bell
const mobileDropdownStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideDown {
    from { transform: translateY(-100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes bellRing {
    0% { transform: rotate(0deg); }
    10% { transform: rotate(14deg); }
    20% { transform: rotate(-12deg); }
    30% { transform: rotate(10deg); }
    40% { transform: rotate(-8deg); }
    50% { transform: rotate(6deg); }
    60% { transform: rotate(-4deg); }
    70% { transform: rotate(2deg); }
    80% { transform: rotate(-1deg); }
    100% { transform: rotate(0deg); }
  }
  @keyframes badgePulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.3); }
    100% { transform: scale(1); }
  }
  .bell-ring {
    animation: bellRing 0.8s ease-in-out;
    transform-origin: top center;
  }
  .badge-pulse {
    animation: badgePulse 0.6s ease-in-out 0.2s;
  }
`;

const ContentHeader: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { activeUserId, currentWorkspace, workspaces, switchWorkspace, createWorkspace } = useWorkspace();
  const { showError, showSuccess, unreadCount } = useNotification();
  const { theme, toggleTheme } = useTheme();
  const isLightTheme = theme === 'light';
  // Force dark header in both light and dark themes
  const headerIsLight = false;
  const { billingInfo, usageStats, refreshBillingInfo } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarVisible, toggleSidebar, isTemplatesPanelOpen, openTemplatesPanel, closeTemplatesPanel } = useSidebar();

  const isTemplatesOpen = isTemplatesPanelOpen;
  // Header always stays dark — no longer blends with template panel background
  const headerBgColor = '#111113';
  const headerBorderClass = 'border-white/5';
  const iconButtonClass = 'text-white/60 hover:text-white hover:bg-white/10';
  const headerPrimaryTextClass = 'text-white';
  const headerSecondaryTextClass = 'text-white/70';
  const mobileMenuItemClass = 'text-white hover:bg-white/10 active:bg-white/15';
  const mobileMenuChevronClass = headerIsLight ? 'text-slate-400' : 'text-white/50';
  const mobileMenuDividerClass = headerIsLight ? 'border-black/10' : 'border-white/10';
  const mobileSectionLabelClass = headerIsLight ? 'text-slate-500' : 'text-white/70';
  const mobileSignOutClass = headerIsLight
    ? 'text-rose-500 hover:bg-rose-50 active:bg-rose-100/70'
    : 'text-red-400 hover:bg-white/10 active:bg-white/15';

  // Helper function to get first name from displayName or email
  const firstName = useMemo(() => {
    if (!currentUser) {
      return 'User';
    }

    // First, try to get from displayName
    if (currentUser.displayName) {
      const trimmedName = currentUser.displayName.trim();
      if (trimmedName.length > 0) {
        const nameParts = trimmedName.split(/\s+/);
        if (nameParts.length > 0 && nameParts[0] && nameParts[0].length > 0) {
          const first = nameParts[0];
          const result = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
          return result;
        }
      }
    }

    // If no displayName, extract from email
    if (currentUser.email) {
      const emailStr = currentUser.email.trim();
      if (emailStr.length > 0 && emailStr.includes('@')) {
        const emailPart = emailStr.split('@')[0].trim();
        if (emailPart && emailPart.length > 0) {
          // Split by common separators (., _, -) and take the first part
          const parts = emailPart.split(/[._-]/);
          let first = parts[0] ? parts[0].trim() : '';

          // If splitting resulted in empty string, use the whole emailPart
          if (!first || first.length === 0) {
            first = emailPart;
          }

          if (first && first.length > 0) {
            // Capitalize first letter, lowercase the rest
            const result = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
            return result;
          }
        }
      }
    }

    return 'User';
  }, [currentUser]);

  // Helper function to get profile image URL (with fallback to generated avatar)
  const profileImageUrl = useMemo(() => {
    if (!currentUser) return null;

    // First, use Firebase Auth photoURL if available
    if (currentUser.photoURL) {
      return currentUser.photoURL;
    }

    // Fallback: Generate avatar from name or email using UI Avatars
    if (currentUser.email) {
      const email = currentUser.email.trim();
      const name = currentUser.displayName || firstName || email.split('@')[0];
      // Use UI Avatars service to generate avatar based on name
      const encodedName = encodeURIComponent(name);
      return `https://ui-avatars.com/api/?name=${encodedName}&background=6366f1&color=fff&size=200&bold=true`;
    }

    return null;
  }, [currentUser, firstName]);

  const [showDropdown, setShowDropdown] = useState(false);
  const [showUsageDropdown, setShowUsageDropdown] = useState(false);
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showWorkspaceSelectionModal, setShowWorkspaceSelectionModal] = useState(false);
  const [workspaceSelectionAction, setWorkspaceSelectionAction] = useState<'campaign' | 'automation' | 'segment'>('campaign');
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [workspaceNameError, setWorkspaceNameError] = useState('');
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const [dropdownImageError, setDropdownImageError] = useState(false);
  const [referralAmount, setReferralAmount] = useState<number>(0);
  const [contactCount, setContactCount] = useState<number | null>(null);
  const [contactCountLoading, setContactCountLoading] = useState(false);
  // const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isRefreshingUsage, setIsRefreshingUsage] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [bellRinging, setBellRinging] = useState(false);
  const prevUnreadCountRef = useRef<number | null>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Load referral credits
  useEffect(() => {
    const loadReferralCredits = async () => {
      if (!currentUser) {
        setReferralAmount(0);
        return;
      }

      try {
        const stats = await referralService.getReferralStats(currentUser.uid);
        setReferralAmount(stats.credits);
      } catch (error) {
        console.error('ContentHeader: Failed to load referral credits', error);
        setReferralAmount(0);
      }
    };

    loadReferralCredits();
  }, [currentUser]);

  // Reset error states when user or profile image URL changes
  useEffect(() => {
    setProfileImageError(false);
    setDropdownImageError(false);
  }, [currentUser, profileImageUrl]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const usageRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);

  const loadContactCount = useCallback(async () => {
    if (!currentUser || !currentWorkspace) {
      setContactCount(null);
      return;
    }

    try {
      setContactCountLoading(true);
      // Use getCountFromServer — 1 read regardless of collection size,
      // instead of downloading every contact doc just to call .length.
      const uid = activeUserId || currentUser.uid;
      const snap = await getCountFromServer(
        query(
          collection(db, 'contacts'),
          where('userId', '==', uid),
          where('workspaceId', '==', currentWorkspace.id)
        )
      );
      setContactCount(snap.data().count);
    } catch (error) {
      console.error('ContentHeader: Failed to load contact count', error);
    } finally {
      setContactCountLoading(false);
    }
  }, [currentUser, activeUserId, currentWorkspace]);

  const handleRefreshUsage = useCallback(async () => {
    if (isRefreshingUsage) return;
    setIsRefreshingUsage(true);
    try {
      await Promise.all([
        refreshBillingInfo(),
        loadContactCount()
      ]);
    } catch (error) {
      console.error('Error refreshing usage:', error);
    } finally {
      setIsRefreshingUsage(false);
    }
  }, [isRefreshingUsage, refreshBillingInfo, loadContactCount]);

  useEffect(() => {
    loadContactCount();
  }, [loadContactCount]);

  useEffect(() => {
    const handleContactsUpdated = () => {
      loadContactCount();
    };

    window.addEventListener('contactsUpdated', handleContactsUpdated);
    return () => {
      window.removeEventListener('contactsUpdated', handleContactsUpdated);
    };
  }, [loadContactCount]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (workspaceRef.current && !workspaceRef.current.contains(event.target as Node)) {
        setShowWorkspaceDropdown(false);
        setShowCreateWorkspace(false);
      }
      if (usageRef.current && !usageRef.current.contains(event.target as Node)) {
        setShowUsageDropdown(false);
      }
      if (createRef.current && !createRef.current.contains(event.target as Node)) {
        setShowCreateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleNewCampaignClick = () => {
    setShowCreateDropdown(false);
    setWorkspaceSelectionAction('campaign');
    setShowWorkspaceSelectionModal(true);
  };

  const handleLaunchAutomationClick = () => {
    setShowCreateDropdown(false);
    setWorkspaceSelectionAction('automation');
    setShowWorkspaceSelectionModal(true);
  };

  const handleCreateSegmentClick = () => {
    setShowCreateDropdown(false);
    setWorkspaceSelectionAction('segment');
    setShowWorkspaceSelectionModal(true);
  };

  const statusInfo = useMemo(() => {
    return subscriptionService.getSubscriptionStatus(billingInfo?.subscription || null);
  }, [billingInfo?.subscription]);

  const effectivePlanId = useMemo(() => {
    return subscriptionService.getEffectivePlanId(billingInfo?.subscription || null);
  }, [billingInfo?.subscription]);

  const emailsSent = usageStats?.emailsSent ?? 0;
  const normalizedPlanId = effectivePlanId.toLowerCase();
  const planConfig = PLAN_CONFIG[normalizedPlanId as keyof typeof PLAN_CONFIG];
  const isStarterPlan = normalizedPlanId === 'starter' || normalizedPlanId === 'free';

  const isExpired = statusInfo.status === 'Expired';
  const isUnlimitedEmailPlan = normalizedPlanId === 'business';

  /** Paid tiers: use live subscriber cap from subscription, not static PLAN_CONFIG defaults. */
  const planSubscriberAllowance =
    normalizedPlanId === 'business'
      ? null
      : normalizedPlanId === 'pro' && billingInfo?.subscription?.subscribers
        ? billingInfo.subscription.subscribers
        : planConfig?.subscribers ?? 1000;

  const emailsRemaining = usageStats?.emailsRemaining;
  const totalEmailAllowance = isUnlimitedEmailPlan
    ? null
    : normalizedPlanId === 'pro' && billingInfo?.subscription?.subscribers
      ? getProMonthlyEmailLimit(billingInfo.subscription.subscribers)
      : planConfig?.emails ?? 12000;

  const remainingEmailsDisplay =
    emailsRemaining !== undefined && !isExpired
      ? emailsRemaining
      : totalEmailAllowance !== null
        ? Math.max(totalEmailAllowance - emailsSent, 0)
        : undefined;

  const usagePercentage = isUnlimitedEmailPlan
    ? 0
    : totalEmailAllowance
      ? Math.round((emailsSent / Math.max(totalEmailAllowance, 1)) * 100)
      : usageStats?.usagePercentage ?? 0;

  const handleWorkspaceSelected = async (workspaceId: string) => {
    // Check if workspace is locked
    const index = workspaces.findIndex(w => w.id === workspaceId);
    const isLocked = isStarterPlan && index > 0;

    try {
      await switchWorkspace(workspaceId);
      setShowWorkspaceSelectionModal(false);
      // ... same switch logic as before ...
      switch (workspaceSelectionAction) {
        case 'automation':
          navigate(`/workspace/${workspaceId}/automations`);
          break;
        case 'segment':
          navigate(`/workspace/${workspaceId}/contacts/segments`);
          break;
        case 'campaign':
        default:
          navigate(`/workspace/${workspaceId}/campaigns/name`);
          break;
      }
    } catch (error) {
      console.error('Error switching workspace:', error);
    }
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    // Check if workspace is locked
    const index = workspaces.findIndex(w => w.id === workspaceId);
    const isLocked = isStarterPlan && index > 0;

    try {
      await switchWorkspace(workspaceId);
      setShowWorkspaceDropdown(false);
      // Navigate to the workspace dashboard
      navigate(`/workspace/${workspaceId}/dashboard`);
    } catch (error) {
      console.error('Error switching workspace:', error);
    }
  };


  const handleWorkspaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewWorkspaceName(e.target.value);
    // Clear error when user starts typing
    if (workspaceNameError) {
      setWorkspaceNameError('');
    }
  };

  const handleWorkspaceAction = async (workspaceId: string, action: 'view' | 'settings') => {
    setShowWorkspaceDropdown(false);
    try {
      // Switch to the target workspace first to ensure context is properly set
      await switchWorkspace(workspaceId);
      if (action === 'settings') {
        navigate(`/workspace/${workspaceId}/settings/general`);
      } else {
        navigate(`/workspace/${workspaceId}`);
      }
    } catch (error) {
      console.error('Error switching workspace:', error);
    }
  };

  const usagePercentageResult = usagePercentage; // dummy usage to keep the logic below clean if needed


  // Email quota warning notification (fires once per session when usage > 80%)
  const quotaWarningFired = useRef(false);
  useEffect(() => {
    if (
      !quotaWarningFired.current &&
      currentUser &&
      !isUnlimitedEmailPlan &&
      totalEmailAllowance &&
      usagePercentage >= 80
    ) {
      quotaWarningFired.current = true;
      NotificationService.sendToUser(
        currentUser.uid,
        'Email quota running low',
        `You've used ${usagePercentage}% of your email allowance (${emailsSent.toLocaleString()} / ${totalEmailAllowance.toLocaleString()} emails). Consider upgrading your plan to avoid hitting your limit.`,
        'warning',
        'system',
        'Scubamail'
      ).catch(err => console.error('Failed to send quota warning notification:', err));
    }
  }, [usagePercentage, currentUser, isUnlimitedEmailPlan, totalEmailAllowance, emailsSent]);

  // Bell ring animation + chime sound when new notifications arrive
  useEffect(() => {
    // Skip the initial render (when prevUnreadCountRef is null)
    if (prevUnreadCountRef.current === null) {
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    // Only trigger when unreadCount increases
    if (unreadCount > prevUnreadCountRef.current) {
      // Trigger bell ring animation
      setBellRinging(true);
      const animTimer = setTimeout(() => setBellRinging(false), 900);

      prevUnreadCountRef.current = unreadCount;
      return () => clearTimeout(animTimer);
    }

    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  const planLabel = useMemo(() => {
    if (planConfig?.name) {
      return planConfig.name;
    }
    return normalizedPlanId.charAt(0).toUpperCase() + normalizedPlanId.slice(1);
  }, [planConfig, normalizedPlanId]);

  const planStatusLabel = statusInfo.status;
  const totalWorkspaces = workspaces?.length ?? 0;
  const contactLimit =
    planSubscriberAllowance != null && planSubscriberAllowance > 0 ? planSubscriberAllowance : null;
  const contactUsagePercentage =
    contactLimit && contactCount !== null
      ? Math.min(Math.round((contactCount / Math.max(contactLimit, 1)) * 100), 100)
      : null;
  const contactRemaining =
    contactLimit && contactCount !== null ? Math.max(contactLimit - contactCount, 0) : null;

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      setWorkspaceNameError('Workspace name is required');
      return;
    }

    // Check if workspace name already exists
    const trimmedName = newWorkspaceName.trim();
    const existingWorkspace = workspaces.find(
      workspace => workspace.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingWorkspace) {
      setWorkspaceNameError('Choose a different name');
      return;
    }

    setIsCreatingWorkspace(true);
    setWorkspaceNameError('');

    try {
      // Pre-check workspace limits
      const { planService } = await import('../services/planService');
      const limitCheck = await planService.canCreateWorkspace(currentUser?.uid || '');
      if (!limitCheck.allowed) {
        // Instead of aggressive red toast, trigger Upgrade Modal
        setShowUpgradeModal(true);
        setWorkspaceNameError(limitCheck.reason || 'Workspace limit reached.');
        return;
      }

      const workspaceId = await createWorkspace({
        name: trimmedName,
        description: '',
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
        timezone: 'UTC',
        currency: 'USD',
        language: 'en'
      });

      setNewWorkspaceName('');
      setWorkspaceNameError('');
      setShowCreateWorkspace(false);
      setShowWorkspaceDropdown(false);

      showSuccess(`Workspace "${trimmedName}" created successfully!`);

      // Switch to the newly created workspace
      await switchWorkspace(workspaceId);
      // Navigate to workspace dashboard
      navigate(`/workspace/${workspaceId}/dashboard`);
    } catch (error: any) {
      console.error('Error creating workspace:', error);

      // Show user-friendly error message
      const errorMessage = error?.message || 'Failed to create workspace. Please try again.';

      // Check for specific error types
      if (errorMessage.includes('limit')) {
        setShowUpgradeModal(true);
      } else if (errorMessage.includes('permission')) {
        showError('Permission denied. Please check your Firebase security rules.', 'Permission Error');
      } else if (errorMessage.includes('unavailable')) {
        showError('Service temporarily unavailable. Please try again.', 'Service Unavailable');
      } else {
        showError(errorMessage, 'Failed to Create Workspace');
      }

      setWorkspaceNameError(errorMessage);
    } finally {
      setIsCreatingWorkspace(false);
    }
  };


  return (
    <>
      {/* Inject animation styles */}
      <style>{mobileDropdownStyles}</style>
      <header className={`relative px-4 sm:px-6 py-3 sm:py-4 h-[60px] sm:h-[73px] flex items-center`} style={{ backgroundColor: headerBgColor }}>
        <div className="flex items-center justify-between w-full">
          {/* Mobile Menu Button and Logo (Left) - Only visible on mobile */}
          <div className="lg:hidden flex items-center space-x-3">
            <button
              onClick={toggleSidebar}
              className={`flex items-center justify-center w-10 h-10 focus:outline-none rounded-lg active:bg-white/20 transition-all duration-200 border touch-manipulation border-white/20 text-white hover:text-gray-200 hover:bg-white/10 active:bg-white/20`}
              title="Toggle sidebar"
              aria-label="Toggle sidebar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Scubamail Logo - Mobile only */}
            <button
              onClick={() => {
                if (currentWorkspace?.id) {
                  navigate(`/workspace/${currentWorkspace.id}/dashboard`);
                } else if (workspaces.length > 0) {
                  navigate(`/workspace/${workspaces[0].id}/dashboard`);
                } else {
                  navigate('/login');
                }
              }}
              className="hover:opacity-80 transition-opacity duration-200"
            >
              <img
                src={"/Scubamal logo for black bg.png"}
                alt="Scubamail Logo"
                width={120}
                height={24}
                className="h-6 w-auto object-contain"
              />
            </button>
          </div>

          <div className="flex items-center space-x-3 flex-1 lg:flex-none">
            {/* Close/Menu Button - Shows when sidebar is hidden (Desktop only) */}
            {!isSidebarVisible && (
              <button
                onClick={toggleSidebar}
                className={`hidden lg:flex items-center justify-center w-10 h-10 focus:outline-none rounded-lg transition-all duration-200 border border-white/20 text-white hover:text-gray-200 hover:bg-white/10`}
                title="Show sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Workspace Switcher - Hidden on mobile, visible on desktop */}
            <div className="hidden lg:block relative" ref={workspaceRef}>
              <button
                onClick={() => setShowWorkspaceDropdown(!showWorkspaceDropdown)}
                className={`flex items-center space-x-2 focus:outline-none px-3 py-2 rounded-lg transition-all duration-200 ${headerIsLight ? 'text-slate-800 hover:text-slate-900 hover:bg-black/5' : 'text-white hover:text-gray-200 hover:bg-white/10'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span className="text-sm font-medium">
                  {currentWorkspace ? currentWorkspace.name : 'Select Workspace'}
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Workspace Dropdown */}
              {showWorkspaceDropdown && (
                <div className="absolute left-0 mt-2 w-96 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-gray-200 dark:border-neutral-600 py-2 z-50">
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-neutral-600">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">Workspaces</h3>
                  </div>

                  <div className="max-h-48 overflow-y-auto">
                    {workspaces.map((workspace, index) => {
                      const isLocked = isStarterPlan && index > 0;
                      return (
                        <div key={workspace.id} className="relative">
                          <div className={`flex items-center gap-2 px-2 py-1.5 text-sm transition-colors duration-200 ${currentWorkspace?.id === workspace.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-neutral-700/70'
                            }`}>
                            <button
                              onClick={() => handleSwitchWorkspace(workspace.id)}
                              className="flex items-center flex-1 min-w-0 text-left space-x-3 rounded-md px-1 py-1"
                            >
                              {/* Workspace Image Placeholder */}
                              <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-neutral-600 dark:bg-neutral-900">
                                {workspace.logo || workspace.settings?.branding?.logo ? (
                                  <img
                                    src={workspace.logo || workspace.settings?.branding?.logo || ''}
                                    alt={workspace.name}
                                    className="h-full w-full object-cover"
                                    onError={(event) => {
                                      const target = event.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center bg-gray-600 dark:bg-neutral-700 text-white">
                                    <span className="text-sm font-semibold">
                                      {workspace.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                {isLocked && (
                                  <div className="absolute -top-1 -right-1 bg-gray-600 rounded-full p-0.5 border border-white">
                                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium block truncate max-w-full">{workspace.name}</span>
                                  {isLocked && (
                                    <svg className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                {currentWorkspace?.id === workspace.id && !isLocked && (
                                  <span className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 block truncate">Current workspace</span>
                                )}
                              </div>

                              {currentWorkspace?.id === workspace.id && !isLocked && (
                                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWorkspaceAction(workspace.id, 'settings');
                              }}
                              className="flex-shrink-0 self-center p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:text-slate-300 dark:hover:text-blue-300 dark:hover:bg-neutral-600 rounded transition-colors duration-200"
                              title="Workspace settings"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-gray-200 dark:border-neutral-600 mt-2">
                    <button
                      onClick={() => setShowCreateWorkspace(true)}
                      className="w-full text-left px-3 py-2 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Create Workspace</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Create Workspace Modal */}
              {showCreateWorkspace && (
                <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-gray-200 dark:border-neutral-600 py-3 z-50">
                  <div className="px-3 pb-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Create New Workspace</h3>
                    <input
                      type="text"
                      placeholder="Workspace name"
                      value={newWorkspaceName}
                      onChange={handleWorkspaceNameChange}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white ${workspaceNameError
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 dark:border-neutral-600 focus:ring-blue-500'
                        }`}
                      autoFocus
                    />
                    {workspaceNameError && (
                      <p className="text-xs text-red-600 mt-1">{workspaceNameError}</p>
                    )}
                  </div>
                  <div className="flex space-x-2 px-3">
                    <button
                      onClick={handleCreateWorkspace}
                      disabled={isCreatingWorkspace || !newWorkspaceName.trim()}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isCreatingWorkspace ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </>
                      ) : (
                        'Create'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateWorkspace(false);
                        setNewWorkspaceName('');
                        setWorkspaceNameError('');
                        setIsCreatingWorkspace(false);
                      }}
                      disabled={isCreatingWorkspace}
                      className="flex-1 bg-gray-200 dark:bg-neutral-600 text-gray-700 dark:text-neutral-300 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-neutral-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3">
            {currentUser && (
              <>
                {/* Scubamail AI Button - Hidden until AI feature is complete
                <div className="hidden md:block">
                  <ScubamailAIButton onClick={() => setIsAIModalOpen(true)} />
                </div>
                */}



                {/* Templates Button - Hidden on mobile */}
                <button
                  onClick={() => {
                    if (isTemplatesOpen) {
                      closeTemplatesPanel();
                    } else {
                      openTemplatesPanel();
                    }
                  }}
                  className={`hidden sm:block text-[13px] font-light text-white/70 hover:text-white transition-colors duration-200 relative group`}
                  style={{ fontFamily: 'Arial, sans-serif' }}
                >
                  Templates
                  <span className={`absolute -bottom-0.5 left-0 h-px bg-white transition-all duration-200 ${isTemplatesOpen ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </button>

                {/* Add a Contact Button - Hidden on mobile */}
                <button
                  onClick={() => setShowAddContactModal(true)}
                  className="hidden sm:block ml-10 text-[13px] font-light text-white/70 hover:text-white transition-colors duration-200 relative group"
                  style={{ fontFamily: 'Arial, sans-serif' }}
                >
                  Add a Contact
                  <span className="absolute -bottom-0.5 left-0 h-px bg-white w-0 group-hover:w-full transition-all duration-200" />
                </button>

                {/* Usage / Plan Dropdown - Hidden on mobile */}
                <div className="hidden md:block relative" ref={usageRef}>
                  <button
                    onClick={() => setShowUsageDropdown(!showUsageDropdown)}
                    className={`group inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 focus:outline-none ${headerIsLight
                      ? 'border-black/10 bg-black/5 text-slate-800 hover:border-black/20 hover:bg-black/10 focus-visible:ring-2 focus-visible:ring-black/20'
                      : 'border-white/30 bg-white/20/50 text-white hover:border-white/60 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/60'
                      }`}
                  >
                    <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400/80 via-emerald-500/80 to-sky-500/80 text-white">
                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M3 3a1 1 0 011-1h3a1 1 0 011 .883L8.5 3l-.002 1H16a1 1 0 01.117 1.993L16 6h-.073A8.002 8.002 0 0110 18H4a1 1 0 01-1-.883L3 17V3zm6.999 4a6.002 6.002 0 014.899 3H11a1 1 0 00-.117 1.993L11 12h4.874A6.01 6.01 0 0110 16a6.002 6.002 0 01-5.917-5.176L4 10.5l.083-.824A6.002 6.002 0 019.999 7z" />
                      </svg>
                      <span className="absolute -right-1 -top-1 inline-flex h-2 w-2 animate-ping rounded-full bg-emerald-200 opacity-75"></span>
                    </span>
                    <div className="flex flex-col items-start leading-tight text-left">
                      <span className={`text-[10px] uppercase tracking-[0.12em] ${headerIsLight ? 'text-slate-500' : 'text-white/70'}`}>Usage</span>
                      <span>Plan overview</span>
                    </div>
                    <ChevronDownIcon
                      className={`ml-2 h-3.5 w-3.5 transition-transform duration-200 ${showUsageDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showUsageDropdown && (
                    <div
                      className="absolute right-0 mt-2 w-[268px] overflow-hidden rounded-2xl bg-white/95 dark:bg-neutral-900/97 shadow-[0_8px_30px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.05)] backdrop-blur-2xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.4),0_0_0_0.5px_rgba(255,255,255,0.04)] z-[99999]"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}
                    >
                      {/* Plan header */}
                      <div className="flex items-center justify-between px-4 pt-4 pb-3.5">
                        <div className="min-w-0">
                          <p className="text-[10px] font-medium tracking-wider text-slate-400 dark:text-neutral-500 uppercase mb-0.5">Current plan</p>
                          <p className="text-[16px] font-semibold tracking-[-0.025em] text-slate-900 dark:text-white leading-none">{planLabel}</p>
                          <p className="mt-1 text-[12px] text-slate-400 dark:text-neutral-500">{emailsSent.toLocaleString()} emails sent</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRefreshUsage(); }}
                          disabled={isRefreshingUsage}
                          className="ml-3 flex-shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-300 dark:text-neutral-600 transition hover:bg-black/5 dark:hover:bg-white/8 hover:text-slate-500 dark:hover:text-neutral-400 disabled:cursor-not-allowed"
                          title="Refresh"
                        >
                          <svg className={`h-3.5 w-3.5 ${isRefreshingUsage ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>

                      {/* Hairline divider */}
                      <div className="h-px mx-4 bg-slate-100 dark:bg-neutral-800" />

                      {/* Metrics list */}
                      <div className="py-1">

                        {/* Emails */}
                        <div className="px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="flex items-center gap-2 text-[13px] text-slate-600 dark:text-neutral-300">
                              <span className="h-[6px] w-[6px] rounded-full bg-blue-500 flex-shrink-0" />
                              Emails
                            </span>
                            <span className="text-[13px] font-medium tabular-nums text-slate-800 dark:text-neutral-100">
                              {emailsSent.toLocaleString()}
                              {isUnlimitedEmailPlan ? (
                                <span className="ml-1.5 text-[11px] font-semibold text-emerald-500">∞</span>
                              ) : (
                                <span className="text-slate-400 dark:text-neutral-500 font-normal"> / {(totalEmailAllowance ?? 0).toLocaleString()}</span>
                              )}
                            </span>
                          </div>
                          {!isUnlimitedEmailPlan && totalEmailAllowance && (
                            <div className="h-[3px] w-full rounded-full bg-slate-100 dark:bg-neutral-800 overflow-hidden">
                              <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(usagePercentage, 100)}%` }} />
                            </div>
                          )}
                          {!isUnlimitedEmailPlan && remainingEmailsDisplay !== undefined && (
                            <p className="mt-1.5 text-[11px] text-slate-400 dark:text-neutral-500">{remainingEmailsDisplay.toLocaleString()} remaining</p>
                          )}
                        </div>

                        <div className="h-px mx-4 bg-slate-100 dark:bg-neutral-800" />

                        {/* Contacts */}
                        <div className="px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="flex items-center gap-2 text-[13px] text-slate-600 dark:text-neutral-300">
                              <span className="h-[6px] w-[6px] rounded-full bg-emerald-500 flex-shrink-0" />
                              Contacts
                            </span>
                            <span className="text-[13px] font-medium tabular-nums text-slate-800 dark:text-neutral-100">
                              {contactCountLoading ? '—' : contactCount !== null ? (
                                <>
                                  {contactCount.toLocaleString()}
                                  {contactLimit ? (
                                    <span className="text-slate-400 dark:text-neutral-500 font-normal"> / {contactLimit.toLocaleString()}</span>
                                  ) : !isStarterPlan ? (
                                    <span className="ml-1.5 text-[11px] font-semibold text-emerald-500">∞</span>
                                  ) : null}
                                </>
                              ) : '—'}
                            </span>
                          </div>
                          {contactLimit && contactCount !== null && (
                            <>
                              <div className="h-[3px] w-full rounded-full bg-slate-100 dark:bg-neutral-800 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${(contactUsagePercentage ?? 0) >= 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
                                  style={{ width: `${Math.min(contactUsagePercentage ?? 0, 100)}%` }}
                                />
                              </div>
                              {contactRemaining !== null && contactRemaining > 0 && (
                                <p className="mt-1.5 text-[11px] text-slate-400 dark:text-neutral-500">{contactRemaining.toLocaleString()} remaining</p>
                              )}
                              {contactRemaining !== null && contactRemaining <= 0 && (
                                <div className="mt-2 flex items-center justify-between">
                                  <p className="text-[11px] text-red-500 dark:text-red-400">Limit reached</p>
                                  <button
                                    onClick={() => { setShowUsageDropdown(false); navigate('/pricing'); }}
                                    className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
                                  >
                                    Upgrade →
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                          {!contactLimit && !isStarterPlan && contactCount !== null && (
                            <p className="text-[11px] text-slate-400 dark:text-neutral-500">Unlimited on your plan</p>
                          )}
                        </div>

                        <div className="h-px mx-4 bg-slate-100 dark:bg-neutral-800" />

                        {/* Workspaces */}
                        <div className="flex items-center justify-between px-4 py-3">
                          <span className="flex items-center gap-2 text-[13px] text-slate-600 dark:text-neutral-300">
                            <span className="h-[6px] w-[6px] rounded-full bg-amber-400 flex-shrink-0" />
                            Workspaces
                          </span>
                          <span className="text-[13px] font-medium tabular-nums text-slate-800 dark:text-neutral-100">{totalWorkspaces}</span>
                        </div>
                      </div>

                      {/* Hairline divider */}
                      <div className="h-px bg-slate-100 dark:bg-neutral-800" />

                      {/* CTA */}
                      <div className="p-3">
                        <button
                          onClick={() => { setShowUpgradeModal(true); setShowUsageDropdown(false); }}
                          className="w-full h-9 rounded-[12px] bg-slate-900 dark:bg-white text-[13px] font-medium text-white dark:text-slate-900 transition hover:opacity-90 active:scale-[0.98]"
                        >
                          Upgrade your plan
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notification Bell Icon */}
                <button
                  ref={bellRef}
                  onClick={() => setShowNotificationCenter(true)}
                  className={`relative p-2 rounded-full transition-all duration-200 ${iconButtonClass} ${bellRinging ? 'bell-ring' : ''}`}
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" strokeWidth={1.9} />
                  {unreadCount > 0 && (
                    <span className={`absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-[10px] font-bold text-white ${bellRinging ? 'badge-pulse' : ''}`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={`group p-2 rounded-full transition-all duration-200 ${iconButtonClass}`}
                  title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                  aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                >
                  {theme === 'light' ? (
                    <MoonStar className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" strokeWidth={1.9} />
                  ) : (
                    <SunMedium className="w-5 h-5 transition-transform duration-300 group-hover:rotate-180" strokeWidth={1.9} />
                  )}
                </button>

                {/* User Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  {/* Mobile Profile Button (Right) - Only visible on mobile */}
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className={`lg:hidden flex items-center justify-center w-10 h-10 focus:outline-none rounded-lg transition-all duration-200 touch-manipulation border ${headerIsLight ? 'border-black/10 text-slate-800 hover:bg-black/5 active:bg-black/10' : 'border-white/20 text-white hover:bg-white/10 active:bg-white/20'}`}
                    title="Profile menu"
                    aria-label="Profile menu"
                  >
                    {profileImageUrl && !profileImageError ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
                        onError={() => setProfileImageError(true)}
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 text-xs font-medium border-2 border-white/20">
                        {firstName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </button>

                  {/* Desktop Profile Button - Hidden on mobile */}
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className={`hidden lg:flex items-center space-x-2 focus:outline-none px-2 py-1.5 rounded-lg transition-all duration-200 ${showDropdown ? (headerIsLight ? 'bg-black/5 text-slate-900' : 'bg-white/10 text-gray-200') : (headerIsLight ? 'text-slate-800 hover:text-slate-900 hover:bg-black/5' : 'hover:text-gray-200 hover:bg-white/10 text-white')
                      }`}
                  >
                    {profileImageUrl && !profileImageError ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                        onError={() => setProfileImageError(true)}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 text-sm font-medium border-2 border-white/20">
                        {firstName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Mobile Full-Screen Dropdown */}
                  {showDropdown && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998]"
                        style={{
                          animation: 'fadeIn 0.3s ease-out forwards'
                        }}
                        onClick={() => setShowDropdown(false)}
                      />

                      {/* Full-Screen Dropdown - Mobile Only */}
                      <div
                        className="lg:hidden fixed inset-0 z-[99999] overflow-y-auto"
                        style={{
                          backgroundColor: headerBgColor,
                          animation: 'slideDown 0.3s ease-out forwards'
                        }}
                      >
                        {/* Header with Close Button */}
                        <div className={`sticky top-0 border-b px-4 py-3 sm:py-4 flex items-center justify-between z-10 ${headerBorderClass}`} style={{ backgroundColor: headerBgColor }}>
                          <h2 className={`text-base sm:text-sm font-semibold ${headerPrimaryTextClass}`}>Profile Menu</h2>
                          <button
                            onClick={() => setShowDropdown(false)}
                            className={`flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 focus:outline-none rounded-lg transition-all duration-200 touch-manipulation ${headerIsLight ? 'text-slate-800 hover:text-slate-900 hover:bg-black/5 active:bg-black/10' : 'text-white hover:text-gray-200 hover:bg-white/10 active:bg-white/20'}`}
                            aria-label="Close menu"
                          >
                            <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {/* User Profile Info */}
                        <div className={`px-4 py-5 sm:py-4 border-b ${headerBorderClass}`}>
                          <div className="flex items-center space-x-4 sm:space-x-3">
                            {profileImageUrl && !dropdownImageError ? (
                              <img
                                src={profileImageUrl}
                                alt="Profile"
                                className="w-14 h-14 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white/20"
                                onError={() => setDropdownImageError(true)}
                              />
                            ) : (
                              <div className="w-14 h-14 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 text-base sm:text-sm font-medium border-2 border-white/20">
                                {firstName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-base sm:text-sm font-medium truncate mb-0.5 ${headerPrimaryTextClass}`}>
                                {firstName}
                              </p>
                              <p className={`text-sm sm:text-xs truncate ${headerSecondaryTextClass}`}>{currentUser?.email || 'User'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate('/account/billing');
                              setShowDropdown(false);
                            }}
                            className={`w-full text-left py-4 sm:py-3 px-4 text-base sm:text-sm transition-colors duration-200 touch-manipulation flex items-center justify-between ${mobileMenuItemClass}`}
                          >
                            <span>My plan</span>
                            <svg className={`w-5 h-5 sm:w-4 sm:h-4 ${mobileMenuChevronClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <div className={`border-t ${mobileMenuDividerClass}`}></div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate('/account/profile');
                              setShowDropdown(false);
                            }}
                            className={`w-full text-left py-4 sm:py-3 px-4 text-base sm:text-sm transition-colors duration-200 touch-manipulation flex items-center justify-between ${mobileMenuItemClass}`}
                          >
                            <span>Your profile</span>
                            <svg className={`w-5 h-5 sm:w-4 sm:h-4 ${mobileMenuChevronClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate('/account/settings');
                              setShowDropdown(false);
                            }}
                            className={`w-full text-left py-4 sm:py-3 px-4 text-base sm:text-sm transition-colors duration-200 touch-manipulation flex items-center justify-between ${mobileMenuItemClass}`}
                          >
                            <span>Account settings</span>
                            <svg className={`w-5 h-5 sm:w-4 sm:h-4 ${mobileMenuChevronClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          <div className={`border-t ${mobileMenuDividerClass} my-2`}></div>
                          <div className="px-4 py-2">
                            <p className={`text-[11px] sm:text-[10px] font-medium uppercase tracking-wider mb-2 ${mobileSectionLabelClass}`}>Credit</p>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate('/refer');
                                setShowDropdown(false);
                              }}
                              className={`w-full text-left py-4 sm:py-3 px-4 text-base sm:text-sm transition-colors duration-200 touch-manipulation flex items-center justify-between rounded-lg -mx-4 ${mobileMenuItemClass}`}
                              style={{ width: 'calc(100% + 2rem)' }}
                            >
                              <span>Refer & Earn</span>
                              <svg className={`w-5 h-5 sm:w-4 sm:h-4 ${mobileMenuChevronClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>

                          <div className={`border-t ${mobileMenuDividerClass}`}></div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleLogout();
                              setShowDropdown(false);
                            }}
                            className={`w-full text-left py-4 sm:py-3 px-4 text-base sm:text-sm transition-colors duration-200 touch-manipulation ${mobileSignOutClass}`}
                          >
                            Sign out
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Desktop Dropdown Menu - Unchanged */}
                  {showDropdown && (
                    <div
                      className={`hidden lg:block absolute right-0 w-96 rounded-xl py-4 px-2 z-[99999] ${headerIsLight ? 'bg-white border border-black/10 shadow-[0_10px_30px_rgba(15,23,42,0.08)]' : 'border border-white/10 shadow-lg'}`}
                      style={{ backgroundColor: headerIsLight ? '#F8FAFC' : '#0E1320', marginTop: '8px' }}
                    >
                      {/* User Profile Info */}
                      <div className={`px-5 py-4 border-b ${headerIsLight ? 'border-black/10' : 'border-white/10'}`}>
                        <div className="flex items-center space-x-3">
                          {profileImageUrl && !dropdownImageError ? (
                            <img
                              src={profileImageUrl}
                              alt="Profile"
                              className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
                              onError={() => setDropdownImageError(true)}
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 text-base font-medium border-2 border-white/20">
                              {firstName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate mb-1 ${headerIsLight ? 'text-slate-900' : 'text-white'}`}>
                              {firstName}
                            </p>
                            <p className={`text-xs ${headerIsLight ? 'text-slate-600' : 'text-white/70'}`}>{currentUser?.email || 'User'}</p>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate('/account/billing');
                          setTimeout(() => {
                            setShowDropdown(false);
                          }, 100);
                        }}
                        className={`w-full text-left py-3 pl-5 pr-5 text-sm transition-colors duration-200 ${headerIsLight ? 'text-slate-800 hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
                        style={{ display: 'block', margin: 0 }}
                      >
                        My plan
                      </button>
                      <div className={`border-t ${headerIsLight ? 'border-black/10' : 'border-white/10'}`}></div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate('/account/profile');
                          setTimeout(() => {
                            setShowDropdown(false);
                          }, 100);
                        }}
                        className={`w-full text-left py-3 pl-5 pr-5 text-sm transition-colors duration-200 ${headerIsLight ? 'text-slate-800 hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
                        style={{ display: 'block', margin: 0 }}
                      >
                        Your profile
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate('/account/settings');
                          setTimeout(() => {
                            setShowDropdown(false);
                          }, 100);
                        }}
                        className={`w-full text-left py-3 pl-5 pr-5 text-sm transition-colors duration-200 ${headerIsLight ? 'text-slate-800 hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
                        style={{ display: 'block', margin: 0 }}
                      >
                        Account settings
                      </button>
                      <div className={`border-t ${headerIsLight ? 'border-black/10' : 'border-white/10'}`}></div>
                      <div className="py-3">
                        <p className={`text-xs font-medium uppercase tracking-wider mb-2 px-5 ${headerIsLight ? 'text-slate-500' : 'text-white/70'}`}>Credit</p>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/refer');
                            setTimeout(() => {
                              setShowDropdown(false);
                            }, 100);
                          }}
                          className={`w-full text-left py-3 pl-5 pr-5 text-sm transition-colors duration-200 flex items-center justify-between ${headerIsLight ? 'text-slate-800 hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
                          style={{ display: 'flex', margin: 0 }}
                        >
                          <span>Refer & Earn</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      <div className={`border-t ${headerIsLight ? 'border-black/10' : 'border-white/10'}`}></div>
                      <button
                        onClick={handleLogout}
                        className={`w-full text-left py-3 pl-5 pr-5 text-sm transition-colors duration-200 ${headerIsLight ? 'text-slate-800 hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
                        style={{ display: 'block', margin: 0 }}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Workspace Selection Modal */}
        <WorkspaceSelectionModal
          isOpen={showWorkspaceSelectionModal}
          onClose={() => setShowWorkspaceSelectionModal(false)}
          onWorkspaceSelected={handleWorkspaceSelected}
          mode={workspaceSelectionAction}
        />

        <SlidingAddContactModal
          isOpen={showAddContactModal}
          onClose={() => setShowAddContactModal(false)}
        />

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />

        {/* Scubamail AI Modal - Hidden until AI feature is complete
        <ScubamailAIModal
          isOpen={isAIModalOpen}
          onClose={() => setIsAIModalOpen(false)}
        />
        */}

        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={() => setShowNotificationCenter(false)}
        />
        <div
          className={`pointer-events-none absolute bottom-0 left-0 right-0 h-px ${headerIsLight ? 'bg-gradient-to-r from-transparent via-black/12 to-transparent' : 'bg-gradient-to-r from-transparent via-white/12 to-transparent'}`}
          aria-hidden="true"
        />
      </header>
    </>
  );
};

export default ContentHeader;